/** 传感智控土壤传感器 — 智能设备管理辅助（与极飞隔离） */

export const MANUFACTURER_MORDENIOT = '传感智控';
export const PLATFORM_MODERNIOT = 'MODERNIOT';
export const TYPE_SOIL_SENSOR = 'SOIL_SENSOR';
export const MODERNIOT_DEVICE_PREFIX = 'MODERNIOT-';
/** 与 application.yml plantai.iot.device-pool-tenant-id 默认一致 */
export const IOT_DEVICE_POOL_TENANT = '__IOT_DEVICE_POOL__';

export function isMordeniotManufacturer(name) {
    const s = String(name || '');
    return s.includes('传感智控') || s.toLowerCase().includes('mordeniot') || s.toLowerCase().includes('moderniot');
}

export function isSoilSensorDevice(device = {}) {
    if (device == null || typeof device !== 'object') return false;
    const type = String(device.deviceType || '').toUpperCase();
    if (type === TYPE_SOIL_SENSOR || type === 'SOIL_STATION') return true;
    if (String(device.thirdPartyPlatform || '').toUpperCase() === PLATFORM_MODERNIOT) return true;
    const code = String(device.deviceCode || device.serialNumber || '');
    return code.toUpperCase().startsWith(MODERNIOT_DEVICE_PREFIX);
}

export function parseJsonSafe(raw) {
    if (!raw) return {};
    if (typeof raw === 'object') return { ...raw };
    try {
        const p = JSON.parse(raw);
        return p && typeof p === 'object' ? p : {};
    } catch {
        return {};
    }
}

export function resolveMordeniotHostId(device = {}) {
    const cfg = parseJsonSafe(device.configJson);
    const m = cfg.mordeniot && typeof cfg.mordeniot === 'object' ? cfg.mordeniot : {};
    const fromCfg = Number(m.hostId);
    if (Number.isFinite(fromCfg) && fromCfg > 0) return fromCfg;
    const fromDid = hostIdFromDeviceId(m.deviceId || device.deviceCode || device.serialNumber);
    return fromDid;
}

export function hostIdFromDeviceId(deviceId) {
    const s = String(deviceId || '').trim();
    if (!s.toUpperCase().startsWith(MODERNIOT_DEVICE_PREFIX)) return 0;
    const n = Number(s.slice(MODERNIOT_DEVICE_PREFIX.length));
    return Number.isFinite(n) && n > 0 ? n : 0;
}

export function buildMordeniotConfigJson(hostId, extra = {}) {
    const id = Number(hostId);
    if (!Number.isFinite(id) || id <= 0) return null;
    return JSON.stringify({
        mordeniot: {
            hostId: id,
            deviceId: `${MODERNIOT_DEVICE_PREFIX}${id}`,
            ...extra
        }
    });
}

/** 规范化单条 passage（兼容 API 原始字段与后端 normalize 字段） */
export function normalizePassageItem(p = {}) {
    const paramName = p.parameterName || p.parameter_name || '';
    const nickname = p.nickname != null && String(p.nickname).trim() !== '' ? String(p.nickname).trim() : '';
    const name = p.name || nickname || paramName || (p.id != null ? `参数${p.id}` : '');
    const rawVal = p.value != null ? p.value : (p.param_value ?? p.paramValue);
    const num = rawVal === '' || rawVal == null ? NaN : Number(rawVal);
    const digit = p.digit != null ? Number(p.digit) : 1;
    return {
        id: p.id,
        name,
        parameterName: paramName || null,
        nickname: nickname || null,
        value: Number.isFinite(num) ? num : null,
        paramValue: rawVal != null ? String(rawVal) : '',
        unit: p.unit || p.en_unit || '',
        type: p.type,
        digit: Number.isFinite(digit) ? digit : 1,
        minValue: p.minValue ?? p.min_value ?? null,
        maxValue: p.maxValue ?? p.max_value ?? null,
        icon: p.icon || ''
    };
}

function formatPassageValue(p) {
    if (p.value != null && Number.isFinite(Number(p.value))) {
        const digit = Number.isFinite(Number(p.digit)) ? Number(p.digit) : 1;
        return Number(p.value).toFixed(Math.max(0, Math.min(4, digit)));
    }
    return p.paramValue || '—';
}

/** 采集间隔：文档单位为秒 */
export function formatCollectionInterval(sec) {
    if (sec == null || sec === '') return null;
    const n = Number(sec);
    if (!Number.isFinite(n) || n < 0) return String(sec);
    if (n >= 3600) {
        const h = Math.floor(n / 3600);
        const m = Math.floor((n % 3600) / 60);
        return m > 0 ? `${n} 秒（约 ${h} 小时 ${m} 分）` : `${n} 秒（约 ${h} 小时）`;
    }
    if (n >= 60) {
        const m = Math.floor(n / 60);
        const s = Math.round(n % 60);
        return s > 0 ? `${n} 秒（约 ${m} 分 ${s} 秒）` : `${n} 秒（约 ${m} 分钟）`;
    }
    return `${n} 秒`;
}

/** 解析 latestStatusJson（含 Apipost real_time/info 的 device_info + passage） */
export function parseSoilLatestStatus(device = {}) {
    const raw = parseJsonSafe(device.latestStatusJson);
    const metrics = raw.metrics && typeof raw.metrics === 'object' ? raw.metrics : {};
    const pick = (k) => {
        const v = raw[k] != null ? raw[k] : metrics[k];
        if (v == null || v === '') return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    };

    let passagesRaw = [];
    if (Array.isArray(raw.passages) && raw.passages.length) passagesRaw = raw.passages;
    else if (Array.isArray(raw.passage) && raw.passage.length) passagesRaw = raw.passage;
    else if (Array.isArray(metrics.passages) && metrics.passages.length) passagesRaw = metrics.passages;
    else if (Array.isArray(metrics.passage) && metrics.passage.length) passagesRaw = metrics.passage;

    const passages = passagesRaw.map(normalizePassageItem).filter((p) => p.name);

    const deviceInfo = raw.deviceInfo && typeof raw.deviceInfo === 'object'
        ? raw.deviceInfo
        : (raw.device_info && typeof raw.device_info === 'object' ? raw.device_info : {});

    const interval = raw.collectionIntervalTime
        ?? deviceInfo.collection_interval_time
        ?? null;

    return {
        soilMoisture: pick('soil_moisture'),
        soilTemp: pick('soil_temp'),
        soilEc: pick('soil_ec'),
        soilPh: pick('soil_ph'),
        soilN: pick('soil_n'),
        soilP: pick('soil_p'),
        soilK: pick('soil_k'),
        illuminance: pick('illuminance'),
        pm1: pick('pm1'),
        pm25: pick('pm25'),
        pm10: pick('pm10'),
        batteryVoltage: pick('battery_voltage') ?? pick('voltage'),
        syncedAt: raw.syncedAt || null,
        online: raw.online,
        status: raw.status || deviceInfo.status || null,
        name: raw.name || deviceInfo.name || null,
        hostId: raw.hostId ?? deviceInfo.id ?? null,
        voltage: raw.voltage ?? deviceInfo.voltage ?? null,
        collectionIntervalTime: interval,
        tsTime: raw.tsTime || deviceInfo.ts_time || null,
        time: raw.time ?? deviceInfo.time ?? null,
        deviceInfo,
        passages
    };
}

export function formatSoilMetricLine(status) {
    if (!status) return '';
    if (Array.isArray(status.passages) && status.passages.length) {
        return status.passages.map((p) => `${p.name} ${formatPassageValue(p)}${p.unit || ''}`).join(' · ');
    }
    const parts = [];
    if (status.soilTemp != null) parts.push(`温度 ${status.soilTemp}℃`);
    if (status.soilMoisture != null) parts.push(`湿度 ${status.soilMoisture}%`);
    if (status.illuminance != null) parts.push(`光照 ${status.illuminance}Lux`);
    if (status.pm1 != null) parts.push(`PM1.0 ${status.pm1}`);
    if (status.pm25 != null) parts.push(`PM2.5 ${status.pm25}`);
    if (status.pm10 != null) parts.push(`PM10 ${status.pm10}`);
    if (status.soilEc != null) parts.push(`EC ${status.soilEc}`);
    if (status.soilPh != null) parts.push(`pH ${status.soilPh}`);
    if (status.batteryVoltage != null) parts.push(`电压 ${status.batteryVoltage}V`);
    return parts.join(' · ');
}

/** 将 syncOneHost / syncedDetails 一条结果转为 latestStatusJson 对象 */
export function buildLatestStatusFromSyncDetail(detail = {}) {
    const hostId = Number(detail.hostId) || 0;
    const metrics = detail.metrics && typeof detail.metrics === 'object' ? detail.metrics : {};
    const passages = Array.isArray(detail.passages)
        ? detail.passages
        : (Array.isArray(metrics.passages) ? metrics.passages : []);
    const deviceInfo = detail.deviceInfo && typeof detail.deviceInfo === 'object'
        ? detail.deviceInfo
        : {};
    return {
        vendor: 'mordeniot',
        hostId,
        deviceId: detail.deviceId || `${MODERNIOT_DEVICE_PREFIX}${hostId}`,
        online: detail.online === true,
        status: detail.status || deviceInfo.status || (detail.online ? 'ONLINE' : 'OFFLINE'),
        name: detail.name || deviceInfo.name || null,
        ts: detail.ts || null,
        tsTime: detail.tsTime || deviceInfo.ts_time || null,
        time: deviceInfo.time ?? null,
        syncedAt: detail.syncedAt || new Date().toISOString(),
        voltage: detail.voltage ?? deviceInfo.voltage ?? null,
        collectionIntervalTime: detail.collectionIntervalTime ?? deviceInfo.collection_interval_time ?? null,
        deviceInfo,
        soil_moisture: metrics.soil_moisture ?? null,
        soil_temp: metrics.soil_temp ?? null,
        soil_ec: metrics.soil_ec ?? null,
        soil_ph: metrics.soil_ph ?? null,
        soil_n: metrics.soil_n ?? null,
        soil_p: metrics.soil_p ?? null,
        soil_k: metrics.soil_k ?? null,
        illuminance: metrics.illuminance ?? null,
        pm1: metrics.pm1 ?? null,
        pm25: metrics.pm25 ?? null,
        pm10: metrics.pm10 ?? null,
        battery_voltage: metrics.battery_voltage ?? detail.voltage ?? deviceInfo.voltage ?? null,
        passages,
        passage: passages,
        metrics
    };
}

/** 将 IoT 遥测 metrics 转为 farm_smart_device.latestStatusJson 结构 */
export function buildLatestStatusFromTelemetry(hostId, telemetry = {}, iotOnline) {
    const metrics = telemetry.metrics && typeof telemetry.metrics === 'object'
        ? telemetry.metrics
        : (typeof telemetry === 'object' ? telemetry : {});
    let passages = Array.isArray(metrics.passages) ? metrics.passages : [];
    if (!passages.length && Array.isArray(metrics.passage)) passages = metrics.passage;
    const deviceInfo = metrics.deviceInfo && typeof metrics.deviceInfo === 'object'
        ? metrics.deviceInfo
        : (metrics.device_info && typeof metrics.device_info === 'object' ? metrics.device_info : {});
    const online = iotOnline === true
        || String(metrics.status || deviceInfo.status || '').toUpperCase() === 'ONLINE';
    return {
        vendor: 'mordeniot',
        hostId,
        deviceId: `${MODERNIOT_DEVICE_PREFIX}${hostId}`,
        online,
        status: metrics.status || deviceInfo.status || (online ? 'ONLINE' : 'OFFLINE'),
        name: deviceInfo.name || null,
        ts: telemetry.ts || null,
        tsTime: deviceInfo.ts_time || null,
        time: deviceInfo.time ?? null,
        syncedAt: new Date().toISOString(),
        voltage: deviceInfo.voltage ?? null,
        collectionIntervalTime: deviceInfo.collection_interval_time ?? null,
        deviceInfo,
        soil_moisture: metrics.soil_moisture ?? null,
        soil_temp: metrics.soil_temp ?? null,
        soil_ec: metrics.soil_ec ?? null,
        soil_ph: metrics.soil_ph ?? null,
        soil_n: metrics.soil_n ?? null,
        soil_p: metrics.soil_p ?? null,
        soil_k: metrics.soil_k ?? null,
        illuminance: metrics.illuminance ?? null,
        pm1: metrics.pm1 ?? null,
        pm25: metrics.pm25 ?? null,
        pm10: metrics.pm10 ?? null,
        battery_voltage: metrics.battery_voltage ?? deviceInfo.voltage ?? null,
        passages,
        passage: passages,
        metrics
    };
}
